import { useEffect, useState } from 'react';
import { SHRINKAGE_REASONS, UNIT_TYPES, formatQty, parseQty, type MovementType } from '@pos/core';
import { applyLocalChanges } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { requestSync } from '../../services/sync.ts';
import { Alert, Button, Field, Input, cx, errorMessage } from '../../ui/index.tsx';
import { Dialog } from '../../ui/Dialog.tsx';
import { buildMovement } from './changes.ts';
import { useStockContext, type StockRow } from './data.ts';

type Kind = 'add' | 'remove' | 'shrinkage' | 'supplier_return' | 'initial';

const KINDS: Record<Kind, { label: string; type: MovementType; sign: 1 | -1; permission: 'adjust_stock' | 'receive_purchases' }> = {
  add: { label: 'Sumar', type: 'adjustment', sign: 1, permission: 'adjust_stock' },
  remove: { label: 'Restar', type: 'adjustment', sign: -1, permission: 'adjust_stock' },
  shrinkage: { label: 'Merma', type: 'shrinkage', sign: -1, permission: 'adjust_stock' },
  supplier_return: { label: 'Devolución a proveedor', type: 'supplier_return', sign: -1, permission: 'receive_purchases' },
  initial: { label: 'Stock inicial', type: 'initial', sign: 1, permission: 'adjust_stock' },
};

/** Ajuste rápido de un producto: sumar/restar con motivo, merma, devolución o stock inicial. */
export function AdjustDialog({ row, onClose }: { row: StockRow | null; onClose: () => void }) {
  const { can, writer } = useSession();
  const context = useStockContext();
  const allowed = (Object.keys(KINDS) as Kind[]).filter(
    (k) => can(KINDS[k].permission) && (k !== 'initial' || (row && !row.tracked)),
  );
  const [kind, setKind] = useState<Kind>('add');
  const [qtyText, setQtyText] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (row) {
      setKind(!row.tracked && can('adjust_stock') ? 'initial' : (allowed[0] ?? 'add'));
      setQtyText('');
      setReason('');
      setError(null);
    }
  }, [row?.variant.id]);

  if (!row) return <Dialog open={false} onClose={onClose} title="">{null}</Dialog>;

  const unit = row.item.product.unit_type;
  const qty = qtyText.trim() ? parseQty(qtyText, unit) : null;
  const def = KINDS[kind];
  const after = qty !== null ? row.qty + def.sign * qty : null;

  const save = async () => {
    // «Stock inicial: 0» vale: empieza el control de stock de un producto que no lo tenía.
    if (qty === null || (qty === 0 && kind !== 'initial')) return setError(`Ingresá una cantidad válida${UNIT_TYPES[unit].fractional ? ' (podés usar decimales)' : ''}.`);
    setBusy(true);
    setError(null);
    try {
      const change = buildMovement({ variantId: row.variant.id, type: def.type, qtyMilli: def.sign * qty, reason }, context());
      await applyLocalChanges(db, [change], writer);
      requestSync();
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={row.label}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={save} loading={busy}>
            Guardar
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Stock actual: <strong>{row.tracked ? formatQty(row.qty, unit) : 'sin control'}</strong>
          {after !== null && (
            <>
              {' '}
              → queda en <strong className={after < 0 ? 'text-red-700' : undefined}>{formatQty(after, unit)}</strong>
            </>
          )}
        </p>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Tipo de movimiento">
          {allowed.map((k) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={kind === k}
              onClick={() => setKind(k)}
              className={cx(
                'rounded-full px-3 py-1.5 text-sm font-medium ring-1',
                kind === k ? 'bg-brand text-brand-fg ring-transparent' : 'bg-white text-slate-700 ring-slate-300',
              )}
            >
              {KINDS[k].label}
            </button>
          ))}
        </div>
        <Field label={`Cantidad${unit !== 'unit' ? ` (${UNIT_TYPES[unit].symbol})` : ''}`}>
          {(id) => <Input id={id} inputMode="decimal" autoFocus value={qtyText} onChange={(e) => setQtyText(e.target.value)} />}
        </Field>
        {kind !== 'initial' && (
          <Field label={def.type === 'supplier_return' ? 'Motivo (opcional)' : 'Motivo'} hint="Queda registrado en la actividad.">
            {(id) => (
              <div className="space-y-2">
                {kind === 'shrinkage' && (
                  <div className="flex flex-wrap gap-1.5">
                    {SHRINKAGE_REASONS.map((r) => (
                      <button key={r} type="button" onClick={() => setReason(r)} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-200">
                        {r}
                      </button>
                    ))}
                  </div>
                )}
                <Input id={id} maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} />
              </div>
            )}
          </Field>
        )}
        {error && <Alert tone="error">{error}</Alert>}
      </div>
    </Dialog>
  );
}
